/**
 * sharing.js — receipt & link sharing via the OS share sheet.
 *
 * On native, backed by `@capacitor/share` which renders the platform
 * share sheet (iOS Activity View / Android chooser). On web we fall
 * back to `navigator.share` when the browser supports it, and to
 * `navigator.clipboard.writeText` when it doesn't so the user can
 * paste the receipt link into their own email.
 *
 * The GreenCharge receipt flow is documented in
 * `src/services/receipts.service.js`. That module continues to own
 * the "email me a receipt" server-side path — this abstraction is for
 * client-side "share this receipt link with…" and PDF export.
 */
import { isNative } from './platform.js';
import { normaliseExternalUrl } from './navigation.js';

let _share = null;
async function sharePlugin() {
  if (_share) return _share;
  const mod = await import('@capacitor/share');
  _share = mod.Share;
  return _share;
}

/**
 * Share text + URL via the OS share sheet.
 * Returns `{ ok: true }` when the OS accepted the payload, or
 * `{ ok: false, reason }` when share isn't supported or the user
 * cancelled. Cancellation is not an error condition; UIs should treat
 * `reason: 'cancelled'` as a silent no-op.
 *
 * @param {{ title?: string, text?: string, url?: string, dialogTitle?: string }} payload
 */
export async function shareContent({ title, text, url, dialogTitle } = {}) {
  const safeUrl = url ? normaliseExternalUrl(url) : undefined;
  if (url && !safeUrl) return { ok: false, reason: 'invalid_url' };

  if (isNative()) {
    const share = await sharePlugin().catch(() => null);
    if (!share) return { ok: false, reason: 'unavailable' };
    try {
      await share.share({ title, text, url: safeUrl, dialogTitle });
      return { ok: true };
    } catch (err) {
      const msg = err?.message || '';
      if (/cancel/i.test(msg)) return { ok: false, reason: 'cancelled' };
      return { ok: false, reason: 'error', message: msg };
    }
  }

  // Web share API (mobile browsers + Chrome desktop w/ flag).
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url: safeUrl });
      return { ok: true };
    } catch (err) {
      if (err?.name === 'AbortError') return { ok: false, reason: 'cancelled' };
      // Fall through to the clipboard fallback.
    }
  }

  // Fallback: copy the URL / text so the user can paste it themselves.
  const clipboardPayload = safeUrl || text || '';
  if (!clipboardPayload) return { ok: false, reason: 'unavailable' };
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(clipboardPayload);
      return { ok: true, clipboard: true };
    }
  } catch { /* fall through */ }
  return { ok: false, reason: 'unavailable' };
}

/**
 * Convenience wrapper for the receipt / session detail page.
 * The URL should be a canonical greencharge.app path such as
 * `https://greencharge.app/history/<sessionUid>` (do NOT share
 * mobile deep-link URLs — recipients might not have the app).
 */
export async function shareReceipt({ sessionUid: _sessionUid, url, energyKWh, cost }) {
  const parts = [];
  if (Number.isFinite(energyKWh)) parts.push(`${energyKWh.toFixed(2)} kWh`);
  if (Number.isFinite(cost)) parts.push(`$${cost.toFixed(2)}`);
  const summary = parts.length ? ` — ${parts.join(', ')}` : '';
  return shareContent({
    title: 'GreenCharge receipt',
    text: `My charging session${summary}`,
    url,
    dialogTitle: 'Share receipt',
  });
}
